import React, { useRef, useEffect, useCallback } from "react"

const Fireworks = ({
    sparkColor = "#ff0000",
    sparkSize = 12,
    sparkRadius = 25,
    sparkCount = 10,
    duration = 500,
    easing = "ease-out",
    extraScale = 1.0,
}) => {
    const canvasRef = useRef(null)
    const sparksRef = useRef([])
    const startTimeRef = useRef(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const resizeCanvas = () => {
            canvas.width = window.innerWidth
            canvas.height = window.innerHeight
        }

        window.addEventListener('resize', resizeCanvas)
        resizeCanvas()

        return () => window.removeEventListener('resize', resizeCanvas)
    }, [])

    const easeFunc = useCallback(
        (t) => {
            switch (easing) {
                case "linear":
                    return t
                case "ease-in":
                    return t * t
                case "ease-in-out":
                    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
                default:
                    return t * (2 - t)
            }
        },
        [easing],
    )

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext("2d")
        if (!ctx) return

        let animationId

        const draw = (timestamp) => {
            if (!startTimeRef.current) {
                startTimeRef.current = timestamp
            }
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            sparksRef.current = sparksRef.current.filter((spark) => {
                const elapsed = timestamp - spark.startTime
                if (elapsed >= duration) {
                    return false
                }

                const progress = elapsed / duration
                const eased = easeFunc(progress)

                const distance = eased * sparkRadius * extraScale
                const lineLength = sparkSize * (1 - eased)

                const x1 = spark.x + distance * Math.cos(spark.angle)
                const y1 = spark.y + distance * Math.sin(spark.angle)
                const x2 = spark.x + (distance + lineLength) * Math.cos(spark.angle)
                const y2 = spark.y + (distance + lineLength) * Math.sin(spark.angle)

                ctx.strokeStyle = sparkColor
                ctx.lineWidth = 2
                ctx.lineCap = "round"
                ctx.beginPath()
                ctx.moveTo(x1, y1)
                ctx.lineTo(x2, y2)
                ctx.stroke()

                return true
            })

            animationId = requestAnimationFrame(draw)
        }

        animationId = requestAnimationFrame(draw)

        return () => {
            cancelAnimationFrame(animationId)
        }
    }, [sparkColor, sparkSize, sparkRadius, sparkCount, duration, easeFunc, extraScale])

    useEffect(() => {
        const handleMouseDown = (e) => {
            const now = performance.now()
            const newSparks = Array.from({ length: sparkCount }, (_, i) => ({
                x: e.clientX,
                y: e.clientY,
                angle: (2 * Math.PI * i) / sparkCount,
                startTime: now,
            }))
            sparksRef.current.push(...newSparks)
        }

        window.addEventListener('mousedown', handleMouseDown)
        return () => window.removeEventListener('mousedown', handleMouseDown)
    }, [sparkCount])

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: "fixed",
                inset: 0,
                pointerEvents: "none",
                zIndex: 999999,
            }}
        />
    )
}

export default Fireworks
